import PropTypes from "prop-types";
import Tooltip from "@shared/v2/Tooltip";
import { CircleInfoSolidV6 } from "../../shared/v2/Icomoon";
import SelectAll from "../components/SelectAll";

const SortableHeader = ({
  label = null,
  orderByname,
  selectedArrow,
  sortKey,
  className = undefined,
  children = null,
}) => (
  <th className={className}>
    {children || (
      <button
        type="button"
        className="tw-p-0 focus:tw-outline-none sort-header tw-bg-transparent tw-border-0"
        onClick={() => orderByname(sortKey)}
      >
        <h6 className="tw-mt-0 tw-mb-0">
          {label}
          <span className="arrows">
            <i className={`fa fa-sort-up ${selectedArrow(sortKey, "asc")}`} />
            <i className={`fa fa-sort-down ${selectedArrow(sortKey, "desc")}`} />
          </span>
        </h6>
      </button>
    )}
  </th>
);

SortableHeader.propTypes = {
  label: PropTypes.node,
  orderByname: PropTypes.func.isRequired,
  selectedArrow: PropTypes.func.isRequired,
  sortKey: PropTypes.string.isRequired,
  className: PropTypes.string,
  children: PropTypes.node,
};

const headerRenderers = ({ orderByname, selectedArrow, source }) => [
  [
    "checkbox",
    <th key="checkbox" id="select-dropdown-header" aria-label="select all">
      <SelectAll />
    </th>,
  ],
  [
    "name",
    <SortableHeader
      key="name"
      label="Name"
      orderByname={orderByname}
      selectedArrow={selectedArrow}
      sortKey="last_name"
    />,
  ],
  [
    "showInLeads",
    source === "leads" ? (
      // eslint-disable-next-line jsx-a11y/control-has-associated-label
      <th key="showInLeads" className="finish-lead">
        &nbsp;
      </th>
    ) : (
      <th key="showInLeads" className="display-in-leads">
        <h6>Show in Leads</h6>
      </th>
    ),
  ],
  [
    "intent",
    <th key="intent" className="intent">
      <h6 className="tw-m-0 tw-pl-[14px]">Intent</h6>
    </th>,
  ],
  [
    "status",
    <SortableHeader
      key="status"
      sortKey="status"
      orderByname={orderByname}
      selectedArrow={selectedArrow}
      className="tw-min-w-[150px] tw-w-[150px] status-header text-left"
    >
      <div className="tw-flex tw-justify-center">
        <button
          type="button"
          className=" tw-p-0 focus:tw-outline-none sort-header tw-bg-transparent tw-border-0"
          onClick={() => orderByname("status")}
        >
          <h6 className="tw-mt-0 tw-mb-0">
            Status
            <span className="arrows">
              <i className={`fa fa-sort-up ${selectedArrow("status", "asc")}`} />
              <i className={`fa fa-sort-down ${selectedArrow("status", "desc")}`} />
            </span>
          </h6>
        </button>
      </div>
    </SortableHeader>,
  ],
  [
    "stage",
    <SortableHeader
      key="stage"
      sortKey="stage"
      orderByname={orderByname}
      selectedArrow={selectedArrow}
      className="tw-min-w-[150px] tw-w-[150px] status-header text-left"
    >
      <div className="tw-flex tw-justify-center tw-mr-[30px]">
        <button
          type="button"
          className="tw-p-0 focus:tw-outline-none sort-header tw-bg-transparent tw-border-0"
          onClick={() => orderByname("stage")}
        >
          <h6 className="tw-mt-0 tw-mb-0">
            Stage
            <Tooltip
              className="tw-pl-[4px]"
              placement="top"
              trigger={<CircleInfoSolidV6 className="tw-text-gray-75 tw-align-sub" size="m" />}
              content="Stages describe where a lead is in your pipeline process"
            />
            <span className="arrows">
              <i className={`fa fa-sort-up ${selectedArrow("stage", "asc")}`} />
              <i className={`fa fa-sort-down ${selectedArrow("stage", "desc")}`} />
            </span>
          </h6>
        </button>
      </div>
    </SortableHeader>,
  ],
  [
    "email",
    <th key="email" className="interaction-icon">
      <h6>Email</h6>
    </th>,
  ],
  [
    "phone",
    <th key="phone" className="interaction-icon">
      <h6>Phone</h6>
    </th>,
  ],
  [
    "sms",
    <th key="sms" className="interaction-icon">
      <h6>SMS</h6>
    </th>,
  ],
  [
    "appointment",
    <th key="appointment" className="interaction-icon">
      <h6>Appt.</h6>
    </th>,
  ],
  [
    "mail",
    <th key="mail" className="interaction-icon">
      <h6>Mail</h6>
    </th>,
  ],
  [
    "other",
    <th key="other" className="interaction-icon">
      <h6>Other</h6>
    </th>,
  ],
  [
    "address",
    <th key="address" className="address">
      <h6>Address</h6>
    </th>,
  ],
  [
    "lastInteraction",
    <SortableHeader
      key="lastInteraction"
      label="Last Interaction"
      orderByname={orderByname}
      selectedArrow={selectedArrow}
      sortKey="last_interaction_interacted_at"
      className="last-interaction text-center"
    />,
  ],
  [
    "nextDueTasks",
    <th key="nextDueTasks">
      <h6 className="tw-pl-[40px]">Next Due Tasks</h6>
    </th>,
  ],
  [
    "primaryAgent",
    <SortableHeader
      key="primaryAgent"
      label="Primary Agent"
      orderByname={orderByname}
      selectedArrow={selectedArrow}
      sortKey="primary_agent_full_name"
      className="primary-agent"
    />,
  ],
  [
    "autoPlans",
    <SortableHeader
      key="autoPlans"
      label="Auto Plans"
      orderByname={orderByname}
      selectedArrow={selectedArrow}
      sortKey="assigned_plans_activity_date"
      className="action-plans text-center"
    />,
  ],
  [
    "birthdate",
    <SortableHeader
      key="birthdate"
      label="Birthdate"
      orderByname={orderByname}
      selectedArrow={selectedArrow}
      sortKey="birthdate"
      className="stat milestone-date-col"
    />,
  ],
  [
    "homeAnniversary",
    <SortableHeader
      key="homeAnniversary"
      label="Home Anniversary"
      orderByname={orderByname}
      selectedArrow={selectedArrow}
      sortKey="anniversary"
      className="large-width-col milestone-date-col"
    />,
  ],
  [
    "homeApp",
    <SortableHeader
      key="homeApp"
      label="Home App"
      orderByname={orderByname}
      selectedArrow={selectedArrow}
      sortKey="home_app_invite_sent"
      className="action-plans text-center"
    />,
  ],
  [
    "hotScore",
    <SortableHeader
      key="hotScore"
      label="Hot Score"
      orderByname={orderByname}
      selectedArrow={selectedArrow}
      sortKey="hot_lead_score"
      className="stat"
    />,
  ],
  [
    "lastVisit",
    <SortableHeader
      key="lastVisit"
      label="Last Visit"
      orderByname={orderByname}
      selectedArrow={selectedArrow}
      sortKey="idx_last_visit"
      className="stat last-visit-col"
    />,
  ],
  [
    "visits",
    <SortableHeader
      key="visits"
      label="Visits"
      orderByname={orderByname}
      selectedArrow={selectedArrow}
      sortKey="idx_visits"
      className="stat"
    />,
  ],
  [
    "views",
    <SortableHeader
      key="views"
      label="Views"
      orderByname={orderByname}
      selectedArrow={selectedArrow}
      sortKey="idx_property_views"
      className="stat"
    />,
  ],
  [
    "avgViewPrice",
    <SortableHeader
      key="avgViewPrice"
      label="Avg. View Price"
      orderByname={orderByname}
      selectedArrow={selectedArrow}
      sortKey="idx_average_price"
      className="barely-wider-stat"
    />,
  ],
  [
    "favorites",
    <SortableHeader
      key="favorites"
      label="Favorites"
      orderByname={orderByname}
      selectedArrow={selectedArrow}
      sortKey="idx_favorites"
      className="stat favorites-col"
    />,
  ],
  [
    "avgFavPrice",
    <SortableHeader
      key="avgFavPrice"
      label="Avg. Fav. Price"
      orderByname={orderByname}
      selectedArrow={selectedArrow}
      sortKey="idx_average_price_favorited"
      className="avg-favorites-col"
    />,
  ],
  [
    "listingAlerts",
    <SortableHeader
      key="listingAlerts"
      label="Listing Alerts"
      orderByname={orderByname}
      selectedArrow={selectedArrow}
      sortKey="idx_alerts"
      className="stat med-width-col"
    />,
  ],
  [
    "lastSent",
    <SortableHeader
      key="lastSent"
      label="Last Sent"
      orderByname={orderByname}
      selectedArrow={selectedArrow}
      sortKey="last_sent_listing_alert"
      className="stat med-width-col"
    />,
  ],
  [
    "lastOpenedAlerts",
    <SortableHeader
      key="lastOpenedAlerts"
      label="Last Opened"
      orderByname={orderByname}
      selectedArrow={selectedArrow}
      sortKey="last_opened_listing_alert"
      className="stat med-width-col"
    />,
  ],
  [
    "marketReports",
    <SortableHeader
      key="marketReports"
      label="Market Reports"
      orderByname={orderByname}
      selectedArrow={selectedArrow}
      sortKey="market_report_count"
      className="med-width-col"
    />,
  ],
  [
    "lastOpenedReports",
    <SortableHeader
      key="lastOpenedReports"
      label="Last Opened"
      orderByname={orderByname}
      selectedArrow={selectedArrow}
      sortKey="last_opened_market_report"
      className="stat med-width-col"
    />,
  ],
  [
    "lastViewedReports",
    <SortableHeader
      key="lastViewedReports"
      label="Last Viewed"
      orderByname={orderByname}
      selectedArrow={selectedArrow}
      sortKey="last_viewed_market_report"
      className="stat med-width-col"
    />,
  ],
  [
    "source",
    <SortableHeader
      key="source"
      label="Source"
      orderByname={orderByname}
      selectedArrow={selectedArrow}
      sortKey="sources.name"
      className="source"
    />,
  ],
  [
    "registered",
    <SortableHeader
      key="registered"
      label="Registered"
      orderByname={orderByname}
      selectedArrow={selectedArrow}
      sortKey="submitted_at"
      className="submitted"
    />,
  ],
  [
    "transactionRole",
    <th key="transactionRole" className="tw-min-w-[140px] tw-w-[140px]">
      <h6 className="tw-ml-[30px] tw-justify-center tw-flex">Transaction Role</h6>
    </th>,
  ],
  [
    "userType",
    <th key="userType" className="tw-min-w-[140px] tw-w-[140px]">
      <button
        type="button"
        className="tw-p-0 focus:tw-outline-none sort-header tw-bg-transparent tw-border-0"
        onClick={() => orderByname("user_type")}
      >
        <h6 className="tw-ml-[30px] tw-mt-0 tw-mb-0 tw-justify-center tw-flex">
          User Type
          <span className="arrows">
            <i className={`fa fa-sort-up ${selectedArrow("user_type", "asc")}`} />
            <i className={`fa fa-sort-down ${selectedArrow("user_type", "desc")}`} />
          </span>
        </h6>
      </button>
    </th>,
  ],
  [
    "tagIcon",
    // eslint-disable-next-line jsx-a11y/control-has-associated-label
    <th key="tagIcon" className="icon">
      &nbsp;
    </th>,
  ],
  [
    "cmaIcon",
    // eslint-disable-next-line jsx-a11y/control-has-associated-label
    <th key="cmaIcon" className="icon">
      &nbsp;
    </th>,
  ],
];

export default headerRenderers;
